import type { FC } from 'react';
import type { Visit } from 'store/visits/types';
import { useDateTimeFormatters } from 'shared/hooks/use-date-time-formatters';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@hqo/ui-atoms';
import { useScheduleText } from 'shared/hooks/use-schedule-text';

interface DeleteModalV2Props {
  handleClose: VoidFunction;
  handleCancelVisit: VoidFunction;
  visitCancelLoading: boolean;
  visitToCancel: Visit;
  visitIsRecurring: boolean;
}

export const DeleteModalV2: FC<DeleteModalV2Props> = ({
  handleClose,
  handleCancelVisit,
  visitCancelLoading,
  visitToCancel,
  visitIsRecurring,
}: DeleteModalV2Props): JSX.Element => {
  const { formatDate, formatTimeRange } = useDateTimeFormatters();
  const { formatMessage } = useIntl();
  const scheduleText = useScheduleText(visitIsRecurring ? visitToCancel : null);
  const visitors = visitToCancel?.visitors ?? [];

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm" data-cy="delete-modal">
        <DialogHeader>
          <DialogTitle>
            {formatMessage({
              id: visitIsRecurring ? 'visitsListV2.cancelVisit.titleRecurring' : 'visitsListV2.cancelVisit.title',
            })}
          </DialogTitle>
          <DialogDescription>
            {visitIsRecurring
              ? formatMessage({ id: 'visitsListV2.cancelVisit.descriptionRecurring' })
              : formatMessage({ id: 'visitsListV2.cancelVisit.description' })}
          </DialogDescription>
        </DialogHeader>
        {visitToCancel && (
          <div className="py-4 space-y-2 text-sm">
            {visitors.length > 1 ? (
              <div>
                <span className="font-medium">
                  {formatMessage(
                    { id: 'labels.visitorNames', defaultMessage: 'Visitor name(s)' },
                    { type: formatMessage({ id: 'labels.visitor.types.guest', defaultMessage: 'Visitor' }) },
                  )}
                  :{' '}
                </span>
                <span>{visitors.map((visitor) => `${visitor.first_name} ${visitor.last_name}`).join(', ')}</span>
              </div>
            ) : (
              <div>
                <span className="font-medium">{formatMessage({ id: 'visitsListV2.cancelVisit.visitor' })}: </span>
                <span>{`${visitToCancel.first_name} ${visitToCancel.last_name}`}</span>
              </div>
            )}
            <div>
              <span className="font-medium">{formatMessage({ id: 'visitsListV2.cancelVisit.date' })}: </span>
              <span>{formatDate(visitToCancel.arrival_date)}</span>
            </div>
            {visitToCancel.arrival_date && visitToCancel.departure_date && (
              <div>
                <span className="font-medium">{formatMessage({ id: 'visitsListV2.cancelVisit.time' })}: </span>
                <span>{formatTimeRange(visitToCancel.arrival_date, visitToCancel.departure_date)}</span>
              </div>
            )}
            {scheduleText && (
              <div>
                <span className="font-medium">{formatMessage({ id: 'visitsListV2.cancelVisit.schedule' })}: </span>
                <span>{scheduleText}</span>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose}>
            {formatMessage({ id: 'visitsListV2.cancelVisit.keepVisit' })}
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancelVisit}
            disabled={visitCancelLoading}
            data-cy="cancel-visit-button"
          >
            {formatMessage({
              id: visitIsRecurring ? 'visitsListV2.cancelVisit.cancelVisits' : 'visitsListV2.cancelVisit.cancelVisit',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
